import React, { useState, useMemo} from 'react';
import { SimpleSelect, SimpleSelectOption } from '@patternfly/react-templates';

export interface SelectProps {
    options: SimpleSelectOption[]
    onSelect?:(_event: React.MouseEvent<Element, MouseEvent> | undefined, value: string | number | undefined) => void
    innerRef?: React.Ref<any>;
    required?: boolean;
    initialValue?: string;
    validated?: 'default' | 'error' | 'warning' | 'success';
    disabled?: boolean;
    placeholder?: string;
    ariaLabel?: string;
    error?: string;
}

export const Select: React.FC<SelectProps> = ({
    options, 
    onSelect, 
    required, 
    innerRef, 
    initialValue,
    validated = 'default',
    disabled = false,
    placeholder,
    ariaLabel,
    error
}) => {
  const [selected, setSelected] = useState<string>(initialValue || "");

  // Sync internal state with external value changes
  React.useEffect(() => {
    setSelected(initialValue || "");
  }, [initialValue]);

  const initial = useMemo(
    () => options.map(o => ({ ...o, selected: o.value === selected })),
    [options, selected]
  );

  const _onSelect = (_e: any, selection: string | number) => {
    const value = typeof selection === 'string' ? selection : selection.toString();
    setSelected(value);
    if (onSelect) {
      onSelect(_e, value);
    }
  };

  // Determine validation state based on error
  const validationState = error ? 'error' : validated;

  return (
    <>
      <SimpleSelect
        innerRef={innerRef}
        initialOptions={initial}
        selected={setSelected}
        onSelect={_onSelect}
        isDisabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
        maxMenuHeight="50vh"
      />
      {error && (
        <div className="dx-helper-text dx-helper-text--error">{error}</div>
      )}
    </>
  );
};

export interface UseSelectOptions {
    options: SimpleSelectOption[];
    required?: boolean;
    requiredMessage?: string;
    invalidSelectionMessage?: string;
    defaultValue?: string;
}

export interface UseSelectReturn {
    value: string;
    error: string;
    handleChange: (value: string) => void;
    handleBlur: () => void;
    validate: () => string;
    setError: (error: string) => void;
    setValue: (value: string) => void;
    isValid: boolean;
}

export const useSelect = (options: UseSelectOptions): UseSelectReturn => {
    const {
        options: selectOptions,
        required = false,
        requiredMessage = 'This field is required',
        invalidSelectionMessage = 'Please select a valid option',
        defaultValue = ''
    } = options;

    const [value, setValue] = React.useState<string>(defaultValue);
    const [error, setError] = React.useState<string>('');

    const validateValue = (val: string): string => {
        if (required && !val.trim()) {
            return requiredMessage;
        }
        
        if (val.trim()) {
            const validOptions = selectOptions.map(option => String(option.value));
            if (!validOptions.includes(val)) {
                return invalidSelectionMessage;
            }
        }
        
        return '';
    };

    const handleChange = (newValue: string) => {
        setValue(newValue);
        // Clear error when user makes a selection
        if (error && newValue) {
            setError('');
        }
    };

    const handleBlur = () => {
        const validationError = validateValue(value);
        setError(validationError);
    };

    const validate = (): string => {
        const validationError = validateValue(value);
        setError(validationError);
        return validationError;
    };

    return {
        value,
        error,
        handleChange,
        handleBlur,
        validate,
        setError,
        setValue,
        isValid: !error && (!required || !!value)
    };
};

// Generic interface for specific select components
export interface BaseSelectComponentProps extends Omit<SelectProps, 'options' | 'onSelect'> {
    value: string;
    onSelect: (value: string) => void;
    // Make sure both required and isRequired are supported for backward compatibility
    isRequired?: boolean;
}

// Factory function to create select components with less boilerplate
export const createSelectComponent = (
    name: string,
    getOptions: () => SimpleSelectOption[]
) => {
    const SelectComponent: React.FC<BaseSelectComponentProps> = ({
        value,
        onSelect,
        isRequired,
        required,
        ...selectProps
    }) => {
        const options = getOptions();

        const handleSelect = (_event: React.MouseEvent<Element, MouseEvent> | undefined, selectedValue: string | number | undefined) => {
            if (selectedValue !== undefined) {
                onSelect(String(selectedValue));
            }
        };

        // Support both required and isRequired for backward compatibility
        const finalRequired = required ?? isRequired ?? false;

        return (
            <Select
                {...selectProps}
                options={options}
                onSelect={handleSelect}
                initialValue={value}
                required={finalRequired}
            />
        );
    };

    SelectComponent.displayName = name;
    return SelectComponent;
};